/* eslint-disable react-refresh/only-export-components */
import { resolveVertical, getVertical } from '@weather-story/vertical-config';
import type { VerticalConfig, VerticalId } from '@weather-story/vertical-config';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

const VerticalContext = createContext<VerticalConfig | null>(null);

/**
 * Resolve the active vertical from the current environment.
 *
 * Priority:
 * 1. `?v=histry` query param (dev override)
 * 2. `window.location.hostname` (production)
 */
function detectVertical(): VerticalConfig {
  if (typeof window === 'undefined') {
    return resolveVertical('weathr.au');
  }

  // Dev override via query param: ?v=histry
  const params = new URLSearchParams(window.location.search);
  const override = params.get('v');
  if (override) {
    return getVertical(override as VerticalId);
  }

  return resolveVertical(window.location.hostname);
}

/**
 * Apply the vertical's theme colors as CSS custom properties on :root.
 * This makes every existing `text-primary-500`, `bg-primary-600`, etc.
 * automatically use the active vertical's color palette.
 */
function applyTheme(config: VerticalConfig) {
  const root = document.documentElement;
  const { primary } = config.theme;

  root.style.setProperty('--vertical-primary-50', primary[50]);
  root.style.setProperty('--vertical-primary-100', primary[100]);
  root.style.setProperty('--vertical-primary-200', primary[200]);
  root.style.setProperty('--vertical-primary-300', primary[300]);
  root.style.setProperty('--vertical-primary-400', primary[400]);
  root.style.setProperty('--vertical-primary-500', primary[500]);
  root.style.setProperty('--vertical-primary-600', primary[600]);
  root.style.setProperty('--vertical-primary-700', primary[700]);
  root.style.setProperty('--vertical-primary-800', primary[800]);
  root.style.setProperty('--vertical-primary-900', primary[900]);
}

/**
 * SVG markup for Lucide icons used as vertical favicons.
 * Auto-generated by: npx tsx apps/web/scripts/build-vertical-assets.ts
 */
const ICON_SVG: Record<string, string> = {
  BookOpen:
    '<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>',
  Cloud: '<path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>',
  Laugh:
    '<circle cx="12" cy="12" r="10"/><path d="M18 13a6 6 0 0 1-6 5 6 6 0 0 1-6-5h12Z"/><line x1="9" x2="9.01" y1="9" y2="9"/><line x1="15" x2="15.01" y1="9" y2="9"/>',
  Utensils:
    '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
  Zap: '<path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>',
};

/**
 * Generate an SVG favicon for the active vertical and inject it into <head>.
 * Uses the vertical's primary color and Lucide icon.
 */
function applyFavicon(config: VerticalConfig) {
  const color = config.theme.primary[500];
  const iconSvg = ICON_SVG[config.icon];

  // Build SVG — rounded square background with the vertical's icon or initial letter
  const iconMarkup = iconSvg
    ? `<g transform="translate(4,4)" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${iconSvg}</g>`
    : `<text x="16" y="23" fill="white" font-family="system-ui,sans-serif" font-size="18" font-weight="bold" text-anchor="middle">${config.name[0]}</text>`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
  <rect width="32" height="32" rx="6" fill="${color}"/>
  ${iconMarkup}
</svg>`;

  const dataUri = `data:image/svg+xml,${encodeURIComponent(svg)}`;

  // Update or create the favicon link element
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    document.head.appendChild(link);
  }
  link.href = dataUri;

  // Remove the static .ico to prevent it overriding the dynamic SVG
  const icoLink = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="48x48"]');
  if (icoLink) icoLink.remove();

  // Swap the apple touch icon per vertical
  const touchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (touchIcon) {
    touchIcon.href =
      config.id === 'weathr'
        ? '/apple-touch-icon-180x180.png'
        : `/apple-touch-icon-${config.id}.png`;
  }
}

/**
 * Update the initial OG meta tags in index.html to match the active vertical.
 * This ensures the correct defaults are in place before react-helmet-async runs.
 */
function applyOgMeta(config: VerticalConfig) {
  const baseUrl = `https://${config.domain}`;
  const ogImageFile = config.id === 'weathr' ? 'og-image.png' : `og-image-${config.id}.png`;

  const updates: Record<string, string> = {
    'og:title': `${config.name} - ${config.tagline}`,
    'og:url': `${baseUrl}/`,
    'og:image': `${baseUrl}/${ogImageFile}`,
    'og:site_name': config.name,
  };

  for (const [prop, content] of Object.entries(updates)) {
    const el = document.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
    if (el) el.content = content;
  }

  // Also update twitter:image
  const twitterImg = document.querySelector<HTMLMetaElement>('meta[name="twitter:image"]');
  if (twitterImg) twitterImg.content = `${baseUrl}/${ogImageFile}`;

  const twitterTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
  if (twitterTitle) twitterTitle.content = `${config.name} - ${config.tagline}`;
}

export function VerticalProvider({ children }: { children: ReactNode }) {
  const vertical = useMemo(() => detectVertical(), []);

  useEffect(() => {
    applyTheme(vertical);
    applyFavicon(vertical);
    applyOgMeta(vertical);
    document.title = `${vertical.name} - ${vertical.tagline}`;
  }, [vertical]);

  return <VerticalContext.Provider value={vertical}>{children}</VerticalContext.Provider>;
}

export function useVertical(): VerticalConfig {
  const ctx = useContext(VerticalContext);
  if (!ctx) {
    throw new Error('useVertical must be used within a VerticalProvider');
  }
  return ctx;
}
